import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { InstallationService } from 'src/app/+installations/services/installation.service';

import {
  ConsolidatedFeature,
  DeviceIds,
  Feature,
  GatewayCommands,
  GatewayProperties,
  IsEnabledProperty,
  IsNumberValueProperty,
  RoomsCommands,
} from '@vi-iot/client';
import { FeatureNames } from '@vi-iot/client';

import {
  defaultPanelsOrder,
  DevicesStore,
  RichDevice,
  FeatureService,
  FEATURES_PANEL_TYPES,
  GatewayStatus,
  InstallationDetailsStore,
  InstallationPanelOrder,
  InstallationFeaturesStore,
} from '@store';

import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { takeUntil } from 'rxjs/operators';
import { DragDropDevices } from '../../../../shared/directives/drag-drop/drag-drop-helper';
import { DragDropLayoutService } from '../../drag-drop-layout/drag-drop-layout.service';

@Component({
  selector: 'app-v2-cascade-feature-tiles',
  templateUrl: './v2-cascade-feature-tiles.component.html',
  styleUrls: ['./v2-cascade-feature-tiles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V2CascadeFeatureTilesComponent implements OnInit {
  @Input() installationId: number;
  @Input() gatewaySerial: string;
  @Input() isLegacyDevice: boolean;

  panelTypes = FEATURES_PANEL_TYPES;
  dashboard = DragDropDevices.Dashboard;
  isSystemPanelVisible$: Observable<boolean>;
  status$: Observable<Feature<GatewayProperties, GatewayCommands>>;
  rooms$: Observable<Feature<IsEnabledProperty, RoomsCommands>>;
  gatewayStatus$: Observable<GatewayStatus>;
  power$: Observable<Feature<IsNumberValueProperty, unknown>>;
  powerConsolidated$: Observable<Feature<ConsolidatedFeature, unknown>>;
  stateOfCharge$: Observable<Feature<IsNumberValueProperty, unknown>>;
  stateOfChargeConsolidated$: Observable<Feature<ConsolidatedFeature, unknown>>;
  productionCurrentConsolidated$: Observable<Feature<ConsolidatedFeature, unknown>>;
  panels: InstallationPanelOrder[];
  isDragDropDisabled: boolean;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    public devicesStore: DevicesStore,
    public featureService: FeatureService,
    public installationService: InstallationService,
    private cdRef: ChangeDetectorRef,
    private dragDropLayoutService: DragDropLayoutService
  ) {}

  ngOnInit(): void {
    this.dragDropLayoutService.isDragDropDisabled
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isDragDropDisabled) => {
        this.isDragDropDisabled = isDragDropDisabled;
        this.cdRef.markForCheck();
      });

    this.isSystemPanelVisible$ = this.featureService.selectIsSystemPanelVisible();
    this.status$ = this.installationFeaturesStore.selectInstallationGatewayFeature(
      FeatureNames.gatewayStatus,
      this.gatewaySerial
    );
    this.power$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.power
    );
    this.powerConsolidated$ = this.featureService.getConsolidatedFeatures(
      FeatureNames.powerConsolidated
    );

    this.stateOfCharge$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.stateOfCharge
    );
    this.stateOfChargeConsolidated$ = this.featureService.getConsolidatedFeatures(
      FeatureNames.stateOfChargeConsolidated
    );

    this.productionCurrentConsolidated$ = this.featureService.getConsolidatedFeatures(
      FeatureNames.productionCurrentConsolidated
    );

    this.rooms$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.rooms,
      DeviceIds.RoomControl
    );

    this.gatewayStatus$ = this.installationDetailsStore.getGatewayStatus();
    this.installationDetailsStore
      .getPanelsOrderForDevice(DragDropDevices.Dashboard)
      .subscribe((panelsOrder: InstallationPanelOrder[]) => {
        if (panelsOrder?.length > 0) {
          this.panels = panelsOrder;
        } else {
          this.panels = defaultPanelsOrder;
        }

        this.cdRef.markForCheck();
      });
  }

  v2DeviceTrackBy(_index: number, device: RichDevice): number {
    return +device.id;
  }

  drop(event: CdkDragDrop<string>): void {
    this.dragDropLayoutService.updatePanelOrderListAfterDrop(
      event.previousContainer.data,
      event.container.data,
      this.panels,
      this.gatewaySerial
    );
  }

  trackBy(_index: number, panel: InstallationPanelOrder): string {
    return panel.panel;
  }
}
